import { useApolloClient } from "@apollo/client";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { isNull, omit } from "lodash-es";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import { ConfirmModal } from "../../../commons/modals/ConfirmModal.js";
import { AddressDetails__AddressFragment } from "../../addresses/Addresses.generated.js";
import { GetAddressModal } from "../../addresses/GetAddressModal.js";
import { isAddressEqual } from "../../addresses/helpers.js";
import { LabeledAddressValue } from "../../addresses/LabeledAddressValue.js";
import { SectionCard } from "../sections/SectionCard.js";
import {
  OrganisationDetailsAddressesBox_ViewerOrganisationFragment,
  useOrganisationDetailsAddressesBox_ModifyOrganisationMutation,
} from "./OrganisationDetailsAddressesBox.generated.js";
import { TFunction } from "@msys/tolgee";

interface Props {
  organisation: OrganisationDetailsAddressesBox_ViewerOrganisationFragment;
  isViewerOrganisation: boolean;
}

interface AddressesFormValues {
  billingAddress: AddressDetails__AddressFragment | null;
  branchAddress: AddressDetails__AddressFragment | null;
  pickupAddress: AddressDetails__AddressFragment | null;
}

type AddressFieldName = keyof AddressesFormValues;
type OptionalAddressFieldName = "branchAddress" | "pickupAddress";
const optionalAddressFields: OptionalAddressFieldName[] = [
  "branchAddress",
  "pickupAddress",
];

interface AddressInlineFieldProps {
  label: string;
  address: AddressDetails__AddressFragment | null;
  onEdit: () => void;
}

const addressLabels = (t: TFunction<"OrganisationProfile">) => ({
  billingAddress: t("Billing address", { ns: "OrganisationProfile" }),
  branchAddress: t("Branch address", { ns: "OrganisationProfile" }),
  pickupAddress: t("Delivery address", { ns: "OrganisationProfile" }),
});

function AddressInlineField({
  label,
  address,
  onEdit,
}: AddressInlineFieldProps) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <LabeledAddressValue label={label} address={address} />
      <IconButton size="small" color="primary" onClick={onEdit}>
        <EditIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

function AddressesForm({
  organisation,
  isViewerOrganisation,
}: {
  organisation: OrganisationDetailsAddressesBox_ViewerOrganisationFragment;
  isViewerOrganisation: boolean;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["OrganisationProfile", "Global"]);
  const client = useApolloClient();

  const [modifyOrganisation] =
    useOrganisationDetailsAddressesBox_ModifyOrganisationMutation({
      client,
      refetchQueries: [namedOperations.Query.OrganisationDetails],
    });

  const [editingAddress, setEditingAddress] = useState<AddressFieldName | null>(
    null
  );
  const [pendingDeleteAddress, setPendingDeleteAddress] =
    useState<OptionalAddressFieldName | null>(null);
  const [showBranchAddress, setShowBranchAddress] = useState(
    !!organisation.branchAddress
  );
  const [showPickupAddress, setShowPickupAddress] = useState(
    !!organisation.pickupAddress
  );
  const [useBillingAsBranch, setUseBillingAsBranch] = useState(true);
  const [useBillingAsPickup, setUseBillingAsPickup] = useState(true);

  const labels = addressLabels(t);

  const isOptionalAddressShown = (field: OptionalAddressFieldName) => {
    if (field === "branchAddress") return showBranchAddress;
    return showPickupAddress;
  };

  const setOptionalAddressShown = (
    field: OptionalAddressFieldName,
    shown: boolean
  ) => {
    if (field === "branchAddress") {
      setShowBranchAddress(shown);
      return;
    }
    setShowPickupAddress(shown);
  };

  const initialValues: AddressesFormValues = useMemo(
    () => ({
      billingAddress: organisation.billingAddress ?? null,
      branchAddress: organisation.branchAddress ?? null,
      pickupAddress: organisation.pickupAddress ?? null,
    }),
    [
      organisation.billingAddress,
      organisation.branchAddress,
      organisation.pickupAddress,
    ]
  );

  const handleSubmit = async (values: AddressesFormValues) => {
    try {
      const input = {
        ...(isViewerOrganisation ? {} : { organisationId: organisation.id }),
        billingAddress: isNull(values.billingAddress)
          ? null
          : values.billingAddress
            ? {
                ...omit(values.billingAddress, "__typename", "id"),
                location: values.billingAddress.location
                  ? omit(values.billingAddress.location, "__typename")
                  : null,
              }
            : undefined,
        branchAddress: isNull(values.branchAddress)
          ? null
          : values.branchAddress
            ? {
                ...omit(values.branchAddress, "__typename", "id"),
                location: values.branchAddress.location
                  ? omit(values.branchAddress.location, "__typename")
                  : null,
              }
            : undefined,
        pickupAddress: isNull(values.pickupAddress)
          ? null
          : values.pickupAddress
            ? {
                ...omit(values.pickupAddress, "__typename", "id"),
                location: values.pickupAddress.location
                  ? omit(values.pickupAddress.location, "__typename")
                  : null,
              }
            : undefined,
      };

      await modifyOrganisation({
        variables: { input },
      });
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
    }
  };

  return (
    <Formik<AddressesFormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {formikProps => (
        <Form>
          <Stack spacing={1}>
            {formikProps.values.billingAddress ? (
              <AddressInlineField
                label={labels.billingAddress}
                address={formikProps.values.billingAddress}
                onEdit={() => {
                  setUseBillingAsBranch(
                    isAddressEqual(
                      formikProps.values.billingAddress,
                      formikProps.values.branchAddress
                    )
                  );
                  setUseBillingAsPickup(
                    isAddressEqual(
                      formikProps.values.billingAddress,
                      formikProps.values.pickupAddress
                    )
                  );
                  setEditingAddress("billingAddress");
                }}
              />
            ) : (
              <Button
                variant="text"
                color="secondary"
                startIcon={<AddIcon />}
                sx={{ alignSelf: "flex-start" }}
                onClick={() => {
                  setUseBillingAsBranch(true);
                  setUseBillingAsPickup(true);
                  setEditingAddress("billingAddress");
                }}
              >
                {t("Add {addressType}", {
                  ns: "OrganisationProfile",
                  addressType: labels.billingAddress,
                })}
              </Button>
            )}

            {optionalAddressFields
              .filter(field => isOptionalAddressShown(field))
              .map(field => (
                <AddressInlineField
                  key={field}
                  label={labels[field]}
                  address={formikProps.values[field]}
                  onEdit={() => setEditingAddress(field)}
                />
              ))}

            {!!formikProps.values.billingAddress &&
              optionalAddressFields
                .filter(field => !isOptionalAddressShown(field))
                .map(field => (
                  <Button
                    key={field}
                    variant="text"
                    color="secondary"
                    startIcon={<AddIcon />}
                    sx={{ alignSelf: "flex-start" }}
                    onClick={() => {
                      setOptionalAddressShown(field, true);
                      setEditingAddress(field);
                    }}
                  >
                    {t("Add {addressType}", {
                      ns: "OrganisationProfile",
                      addressType: labels[field],
                    })}
                  </Button>
                ))}

            <AutoSave />
          </Stack>

          {editingAddress && (
            <GetAddressModal
              title={labels[editingAddress]}
              inputLabel={labels[editingAddress]}
              value={formikProps.values[editingAddress]}
              handleClose={() => {
                if (editingAddress && editingAddress !== "billingAddress") {
                  const optionalField =
                    editingAddress as OptionalAddressFieldName;
                  if (!formikProps.values[optionalField]) {
                    setOptionalAddressShown(optionalField, false);
                  }
                }
                setEditingAddress(null);
              }}
              hideCancel={editingAddress === "billingAddress"}
              handleComplete={async address => {
                if (editingAddress === "billingAddress") {
                  await formikProps.setFieldValue("billingAddress", address);
                  if (useBillingAsBranch) {
                    setOptionalAddressShown("branchAddress", true);
                    await formikProps.setFieldValue("branchAddress", address);
                  } else {
                    setOptionalAddressShown("branchAddress", false);
                    await formikProps.setFieldValue("branchAddress", null);
                  }
                  if (useBillingAsPickup) {
                    setOptionalAddressShown("pickupAddress", true);
                    await formikProps.setFieldValue("pickupAddress", address);
                  } else {
                    setOptionalAddressShown("pickupAddress", false);
                    await formikProps.setFieldValue("pickupAddress", null);
                  }
                } else {
                  setOptionalAddressShown(editingAddress, true);
                  await formikProps.setFieldValue(editingAddress, address);
                }
                setEditingAddress(null);
              }}
              secondaryAction={
                editingAddress !== "billingAddress" &&
                !!formikProps.values[editingAddress]
                  ? {
                      label: t("Delete", { ns: "Global" }),
                      buttonProps: {
                        startIcon: <DeleteIcon />,
                      },
                      handleAction: async handleClose => {
                        setPendingDeleteAddress(
                          editingAddress as OptionalAddressFieldName
                        );
                        handleClose();
                      },
                    }
                  : undefined
              }
              extraContent={
                editingAddress === "billingAddress" ? (
                  <Stack>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={useBillingAsBranch}
                          onChange={(_, checked) => {
                            setUseBillingAsBranch(checked);
                          }}
                        />
                      }
                      label={t("Use as {addressType}", {
                        ns: "OrganisationProfile",
                        addressType: labels.branchAddress,
                      })}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={useBillingAsPickup}
                          onChange={(_, checked) => {
                            setUseBillingAsPickup(checked);
                          }}
                        />
                      }
                      label={t("Use as {addressType}", {
                        ns: "OrganisationProfile",
                        addressType: labels.pickupAddress,
                      })}
                    />
                  </Stack>
                ) : null
              }
            />
          )}

          {pendingDeleteAddress && (
            <ConfirmModal
              title={t("Confirm deletion", { ns: "Global" })}
              text={
                pendingDeleteAddress === "branchAddress"
                  ? t("Are you sure you want to delete the {addressType}?", {
                      ns: "OrganisationProfile",
                      addressType: labels.branchAddress,
                    })
                  : t("Are you sure you want to delete the {addressType}?", {
                      ns: "OrganisationProfile",
                      addressType: labels.pickupAddress,
                    })
              }
              confirmButtonLabel={t("Delete", { ns: "Global" })}
              handleClose={() => setPendingDeleteAddress(null)}
              handleConfirm={async () => {
                const fieldToDelete = pendingDeleteAddress;
                if (!fieldToDelete) return;

                setOptionalAddressShown(fieldToDelete, false);
                await formikProps.setFieldValue(fieldToDelete, null);
                setPendingDeleteAddress(null);
              }}
            />
          )}
        </Form>
      )}
    </Formik>
  );
}

function AddressesView({
  organisation,
}: {
  organisation: OrganisationDetailsAddressesBox_ViewerOrganisationFragment;
}) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);

  return (
    <>
      <LabeledAddressValue
        label={addressLabels(t).billingAddress}
        address={organisation.billingAddress}
      />
      <LabeledAddressValue
        label={addressLabels(t).branchAddress}
        address={organisation.branchAddress}
      />
      <LabeledAddressValue
        label={addressLabels(t).pickupAddress}
        address={organisation.pickupAddress}
      />
    </>
  );
}

export function OrganisationDetailsAddressesBox({
  organisation,
  isViewerOrganisation,
}: Props) {
  const { t } = useTranslate(["OrganisationProfile"]);
  const viewer = useUserData().currentUser!;

  return (
    <SectionCard title={t("List of addresses", { ns: "OrganisationProfile" })}>
      <RestrictedByOrganisationPermissionWithDebug
        permission="MANAGE_ORG"
        otherwise={<AddressesView organisation={organisation} />}
      >
        {viewer.organisation.id === organisation.id &&
        !!viewer.roles.find(r => r.internalName === "ORG_ADMIN") ? (
          <AddressesForm
            organisation={organisation}
            isViewerOrganisation={isViewerOrganisation}
          />
        ) : (
          <AddressesView organisation={organisation} />
        )}
      </RestrictedByOrganisationPermissionWithDebug>
    </SectionCard>
  );
}
